/**
 * CustomCssContext for UI-Model-1 (API-Direct Approach)
 *
 * Thin wrapper context that:
 * - Reads customCss data from UIModelStateContext (state.model.customCss)
 * - Dispatches updates via UIModelStateContext
 * - Provides domain-specific convenience methods
 * - Maintains local editor state (isEditing, validationErrors)
 *
 * Line limit: ~100 lines
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { DEFAULT_CUSTOM_CSS } from '../types/state.types';
import { CustomCssContextValue } from '../types/context.types';
import { useUIModelState } from './UIModelStateContext';

// ============================================================================
// Context
// ============================================================================

const CustomCssContext = createContext<CustomCssContextValue | null>(null);

export const CustomCssProvider = ({ children }: { children: React.ReactNode }) => {
  const { state, dispatch } = useUIModelState();

  // Local editor state (not persisted to API)
  const [isEditing, setIsEditing] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // ============================================================================
  // Derived State (from UIModelState)
  // ============================================================================

  const customCss = useMemo<Record<string, unknown> | null>(() => {
    if (!state.model?.customCss) return null;
    return state.model.customCss;
  }, [state.model?.customCss]);

  // ============================================================================
  // Update Operations
  // ============================================================================

  const updateCss = useCallback((cssContent: string) => {
    if (!state.model) return;

    const currentCss = state.model.customCss || DEFAULT_CUSTOM_CSS;

    const newModel = {
      ...state.model,
      customCss: {
        ...currentCss,
        css: cssContent,
        globalStyles: cssContent, // Keep both in sync
      },
    };

    dispatch({ type: 'SET_MODEL', payload: newModel });
    dispatch({ type: 'MARK_DIRTY', payload: { section: 'customCss' } });
  }, [state.model, dispatch]);

  const updateCssEnabled = useCallback((enabled: boolean) => {
    if (!state.model) return;

    const currentCss = state.model.customCss || DEFAULT_CUSTOM_CSS;

    const newModel = {
      ...state.model,
      customCss: {
        ...currentCss,
        enabled,
      },
    };

    dispatch({ type: 'SET_MODEL', payload: newModel });
    dispatch({ type: 'MARK_DIRTY', payload: { section: 'customCss' } });
  }, [state.model, dispatch]);

  const resetCss = useCallback(() => {
    if (!state.model) return;

    const newModel = {
      ...state.model,
      customCss: DEFAULT_CUSTOM_CSS,
    };

    dispatch({ type: 'SET_MODEL', payload: newModel });
    dispatch({ type: 'MARK_DIRTY', payload: { section: 'customCss' } });
    setValidationErrors([]);
  }, [state.model, dispatch]);

  const validateCss = useCallback((css: string): boolean => {
    const errors: string[] = [];

    // Basic CSS validation
    const openBraces = (css.match(/{/g) || []).length;
    const closeBraces = (css.match(/}/g) || []).length;

    if (openBraces !== closeBraces) {
      errors.push('Mismatched braces in CSS');
    }

    // Check for unclosed strings
    const singleQuotes = (css.match(/'/g) || []).length;
    const doubleQuotes = (css.match(/"/g) || []).length;

    if (singleQuotes % 2 !== 0) {
      errors.push('Unclosed single quote');
    }
    if (doubleQuotes % 2 !== 0) {
      errors.push('Unclosed double quote');
    }

    setValidationErrors(errors);
    return errors.length === 0;
  }, []);

  // ============================================================================
  // Context Value
  // ============================================================================

  const value = useMemo<CustomCssContextValue>(
    () => ({
      customCss,
      isEditing,
      validationErrors,
      updateCss,
      updateCssEnabled,
      resetCss,
      validateCss,
      setIsEditing,
    }),
    [customCss, isEditing, validationErrors, updateCss, updateCssEnabled, resetCss, validateCss]
  );

  return (
    <CustomCssContext.Provider value={value}>
      {children}
    </CustomCssContext.Provider>
  );
};

export const useCustomCss = (): CustomCssContextValue => {
  const context = useContext(CustomCssContext);
  if (!context) {
    throw new Error('useCustomCss must be used within a CustomCssProvider');
  }
  return context;
};

export default CustomCssContext;
